import { useMemo } from "react";
import Fuse from "fuse.js";
import { typeSelection, countrySelection, regionSelection } from "@/lib/shared/constants";

export type FilterType = "brand" | "vendor" | "type" | "region" | "country" | "preset";
export type Suggestion = { key: FilterType; option: Filter };

const fuseOptions = { keys: ["label"], threshold: 0.3 };

// Default filter types (without region)
const DEFAULT_FILTER_TYPES: FilterType[] = ["brand", "vendor", "type", "country"];
// All filter types including region
const ALL_FILTER_TYPES: FilterType[] = ["brand", "vendor", "type", "region", "country"];
// All filter types including presets
// sets priority order of suggestions in dropdown
const ALL_FILTER_TYPES_WITH_PRESETS: FilterType[] = [
  "preset",
  "brand",
  "type",
  "region",
  "country",
  "vendor",
];

interface UseFilterSearchProps {
  brands: Filter[] | undefined;
  vendors: Filter[] | undefined;
  presets?: Filter[] | undefined;
  query: string;
  activeFilters?: Record<string, Filter[]>;
  maxPerType?: number;
  includeRegions?: boolean;
  includePresets?: boolean;
}

// Map filter types to store dropdown names
export const FILTER_TYPE_TO_DROPDOWN: Record<FilterType, string> = {
  brand: "brands",
  vendor: "vendors",
  type: "type",
  region: "regions",
  country: "regions",
  preset: "presets",
};

/**
 * Hook for searching filters with fuzzy matching.
 *
 * Uses Fuse.js to provide fuzzy search across brands, vendors, types,
 * regions, countries, and presets. Excludes already-active filters from
 * suggestions.
 *
 * @param props - Configuration object
 * @param props.brands - Available brand filters
 * @param props.vendors - Available vendor filters
 * @param props.presets - Available preset filters (optional)
 * @param props.query - Search query string
 * @param props.activeFilters - Currently active filters to exclude from results
 * @param props.maxPerType - Maximum results per filter type (default: 5)
 * @param props.includeRegions - Whether to include region filters (default: false)
 * @param props.includePresets - Whether to include preset filters (default: false)
 * @returns Object containing:
 * - suggestions: Array of filter suggestions with type and option
 * - fuse: Fuse search instances for each filter type
 *
 * @example
 * ```tsx
 * function FilterSearch({ brands, vendors, activeFilters }) {
 *   const [query, setQuery] = useState("");
 *   const { suggestions } = useFilterSearch({
 *     brands,
 *     vendors,
 *     query,
 *     activeFilters,
 *     maxPerType: 3,
 *   });
 *
 *   return (
 *     <div>
 *       <Input
 *         value={query}
 *         onChange={e => setQuery(e.target.value)}
 *         placeholder="Search filters..."
 *       />
 *       {suggestions.map(s => (
 *         <SuggestionItem
 *           key={`${s.key}-${s.option.value}`}
 *           onClick={() => addFilter(s.key, s.option)}
 *         >
 *           {s.option.label}
 *         </SuggestionItem>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFilterSearch({
  brands,
  vendors,
  presets,
  query,
  activeFilters,
  maxPerType = 5,
  includeRegions = false,
  includePresets = false,
}: UseFilterSearchProps) {
  // Build Fuse search indexes
  const fuse = useMemo(
    () => ({
      brand: new Fuse<Filter>(brands || [], fuseOptions),
      vendor: new Fuse<Filter>(vendors || [], fuseOptions),
      type: new Fuse<Filter>(typeSelection || [], fuseOptions),
      region: new Fuse<Filter>(regionSelection || [], fuseOptions),
      country: new Fuse<Filter>(countrySelection || [], fuseOptions),
      preset: new Fuse<Filter>(presets || [], fuseOptions),
    }),
    [brands, vendors, presets]
  );

  const filterTypes = includePresets
    ? ALL_FILTER_TYPES_WITH_PRESETS
    : includeRegions
      ? ALL_FILTER_TYPES
      : DEFAULT_FILTER_TYPES;

  // Generate suggestions based on query
  const suggestions: Suggestion[] = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return [];
    const all: Suggestion[] = [];

    filterTypes.forEach((key) => {
      fuse[key]
        .search(q)
        .slice(0, maxPerType)
        .forEach((r) => {
          // Skip if already in active filters (when provided)
          if (activeFilters) {
            const dropdownName = FILTER_TYPE_TO_DROPDOWN[key];
            const isAlreadyActive = activeFilters[dropdownName]?.some(
              (f: Filter) =>
                String(f.value) === String(r.item.value) ||
                String(f.id) === String(r.item.id) ||
                f.label === r.item.label
            );
            if (isAlreadyActive) return;
          }
          all.push({ key, option: r.item });
        });
    });
    return all;
  }, [query, fuse, activeFilters, maxPerType, filterTypes]);

  return { suggestions, fuse };
}

/**
 * Hook for generating brand names for the typewriter effect.
 *
 * The order is deliberately stable so server rendering and hydration produce
 * identical text. The animation itself provides the visual variety.
 *
 * @param brands - Array of brand filters
 * @returns Array of brand name strings suitable for typewriter display
 *
 * @example
 * ```tsx
 * function SearchHero() {
 *   const { brands } = useGlobalData();
 *   const strings = useTypewriterStrings(brands);
 *
 *   return (
 *     <Typewriter
 *       strings={strings}
 *       loop
 *       cursor
 *     />
 *   );
 * }
 * ```
 */
export function useTypewriterStrings(brands: Filter[] | undefined) {
  return useMemo(() => {
    const defaults = ["Raf Simons", "Balenciaga", "Dior", "Gucci", "Prada", "Margiela"];
    const brandNames = brands?.map((brand) => brand.label) || defaults;
    return brandNames.filter((brand) => brand && brand.length <= 15);
  }, [brands]);
}

/**
 * Hook for generating a single static placeholder brand name.
 *
 * Returns the first short brand name (<= 12 chars) for use as a stable static
 * placeholder across server rendering and hydration.
 * Defaults to "Raf Simons" if no suitable brands found.
 *
 * @param brands - Array of brand filters
 * @returns A single brand name string
 *
 * @example
 * ```tsx
 * function SearchInput() {
 *   const { brands } = useGlobalData();
 *   const placeholder = useStaticPlaceholder(brands);
 *
 *   return (
 *     <input
 *       type="text"
 *       placeholder={`Search ${placeholder}...`}
 *     />
 *   );
 * }
 * ```
 */
export function useStaticPlaceholder(brands: Filter[] | undefined) {
  return useMemo(() => {
    const defaults = ["Raf Simons", "Balenciaga", "Dior"];
    const shortBrands = (brands || []).filter((b) => b.label && b.label.length <= 12);
    if (shortBrands.length === 0) return defaults[0];
    return shortBrands[0]?.label || defaults[0];
  }, [brands]);
}
